import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { DatePicker } from './ui/DatePicker';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { TaxSlabRow, TaxSlab } from './TaxSlabRow';
interface TaxProfileFormProps {
  onBack: () => void;
  onSave: () => void;
}
export function TaxProfileForm({
  onBack,
  onSave
}: TaxProfileFormProps) {
  // Generate a random UUID for display
  const [profileId] = useState(`TAX-${Math.random().toString(36).substr(2, 9).toUpperCase()}`);
  // Header State
  const [headerData, setHeaderData] = useState({
    code: '',
    name: '',
    effectiveDate: '',
    tax: ''
  });
  // Slabs State
  const [slabs, setSlabs] = useState<TaxSlab[]>([{
    id: Math.random().toString(36).substr(2, 9),
    taxType: 'Percentage',
    taxValue: '',
    slabLow: '0',
    slabHigh: '',
    filerStatus: 'Both',
    glAccount: ''
  }]);
  const handleHeaderChange = (field: string, value: string) => {
    setHeaderData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleAddSlab = () => {
    const lastSlab = slabs[slabs.length - 1];
    const newLow = lastSlab && lastSlab.slabHigh ? (parseInt(lastSlab.slabHigh) + 1).toString() : '0';
    setSlabs([...slabs, {
      id: Math.random().toString(36).substr(2, 9),
      taxType: 'Percentage',
      taxValue: '',
      slabLow: newLow,
      slabHigh: '',
      filerStatus: 'Both',
      glAccount: ''
    }]);
  };
  const handleRemoveSlab = (id: string) => {
    if (slabs.length > 1) {
      setSlabs(slabs.filter(s => s.id !== id));
    }
  };
  const handleSlabChange = (id: string, updates: Partial<TaxSlab>) => {
    setSlabs(slabs.map(s => s.id === id ? {
      ...s,
      ...updates
    } : s));
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-1089">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-1090">
        <div className="flex items-center" data-id="element-1091">
          <Button variant="ghost" onClick={onBack} className="mr-4" data-id="element-1092">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-1093" />
            Back to List
          </Button>
          <div data-id="element-1094">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-1095">New Tax Profile</h1>
            <p className="text-sm text-gray-500" data-id="element-1096">
              Configure tax rates and slab definitions
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-1097">
          <Button variant="secondary" onClick={onBack} data-id="element-1098">
            Cancel
          </Button>
          <Button onClick={onSave} data-id="element-1099">
            <Save className="h-4 w-4 mr-2" data-id="element-1100" />
            Save Profile
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-8" data-id="element-1101">
        {/* Section 1: Profile Header */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1102">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-1103">
            Profile Header
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1104">
            <div className="space-y-1.5" data-id="element-1105">
              <label className="text-sm font-medium text-gray-700" data-id="element-1106">
                Product Tax ID
              </label>
              <div className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-id="element-1107">
                {profileId}
              </div>
              <p className="text-xs text-gray-500" data-id="element-1108">
                Auto-generated unique identifier
              </p>
            </div>

            <Input label="Product Tax Code" value={headerData.code} onChange={e => handleHeaderChange('code', e.target.value)} placeholder="e.g., TAX-VAT-001" helperText="Used for integration routing" required data-id="element-1109" />

            <Input label="Product Tax Name" value={headerData.name} onChange={e => handleHeaderChange('name', e.target.value)} placeholder="e.g., Standard VAT 18%" helperText="Display name for product setup selection" required data-id="element-1110" />

            <DatePicker label="Effective Date" value={headerData.effectiveDate} onChange={e => handleHeaderChange('effectiveDate', e.target.value)} helperText="Date when this tax profile becomes applicable" required data-id="element-1111" />

            <div className="md:col-span-2" data-id="element-1112">
              <Select label="Tax" value={headerData.tax} onChange={e => handleHeaderChange('tax', e.target.value)} options={[{
              value: 'Service Tax',
              label: 'Service Tax'
            }, {
              value: 'Withholding Tax',
              label: 'Withholding Tax'
            }, {
              value: 'FED Tax',
              label: 'FED Tax'
            }, {
              value: 'VAT Tax',
              label: 'VAT Tax'
            }]} helperText="Defines the type of tax applied" required data-id="element-1113" />
            </div>
          </div>
        </section>

        {/* Section 2: Tax Slab Configuration */}
        <section className="space-y-4 animate-in fade-in slide-in-from-bottom-6 duration-500 delay-100" data-id="element-1114">
          <div className="flex items-center justify-between" data-id="element-1115">
            <div data-id="element-1116">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1117">
                Tax Slab Configuration
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1118">
                Define multiple slabs based on amount ranges and filer status
              </p>
            </div>
            <Button onClick={handleAddSlab} size="sm" variant="secondary" data-id="element-1119">
              <Plus className="h-4 w-4 mr-2" data-id="element-1120" />
              Add Slab
            </Button>
          </div>

          <div className="space-y-4" data-id="element-1121">
            {slabs.map((slab, index) => <TaxSlabRow key={slab.id} slab={slab} index={index} onChange={handleSlabChange} onRemove={handleRemoveSlab} data-id="element-1122" />)}
          </div>
        </section>
      </div>
    </div>;
}